import { cn } from '@mlm/ui';

/**
 * A small enclosed pill control for switching between 2–3 mutually exclusive
 * view modes (ledger filter, genealogy view). This is the right affordance for
 * a view switch, distinct from the underline status-tabs the Orders page uses
 * for navigation across many statuses. Color-only transitions and a visible
 * focus ring keep it keyboard-legible.
 */
export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  className,
  ariaLabel,
}: {
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  className?: string;
  ariaLabel?: string;
}) {
  return (
    <div role="tablist" aria-label={ariaLabel} className={cn('flex gap-2', className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={cn(
              'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              active
                ? 'border-primary bg-primary text-primary-foreground'
                : 'bg-card text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
